<template>
  <div class="sensor-form" v-if="show">
    <page-title-area>
      <entity-header :icon="sensor.type" v-if="new_record">
        {{ $mpt($t('label.new_sensor')) }}
      </entity-header>
      <entity-header :details="featureIsActive('monitoring_point') ? null : sensor.place" v-else>
        <template #icon><sensor-icon :sensorType="sensor.type" /></template>
        {{ sensor.name }}
        <status-chip
          v-if="featureIsActive('monitoring_point')"
          :status="sensor.status"
          class="ml-2"
        />
      </entity-header>
      <template #right-floating-content>
        <counter :count="count" v-if="!new_record" />
      </template>
    </page-title-area>
    <div class="sensor-form__content" data-testid="SensorForm">
      <div class="sensor-form__form my-3">
        <v-form v-model="valid" ref="form">
          <form-group :required="state.editting">
            <template #label><span>{{ $mpt(identifierLabel) }}</span></template>
            <template #input>
              <text-field
                class="sensor-form__form__id"
                :value="displayedSensorId"
                @input="updateId"
                :edit="state.editting && new_record"
                :rules="rules.id"
                :hint="identifierHint"
                :errorMessages="state.idErrors"
                data-testid="sensor_identifier"
              />
            </template>
          </form-group>
          <form-group v-if="capabilities.receives_mails && !new_record">
            <template #label><span>{{ $t('field.mail_address') }}</span></template>
            <template #input>
              <div data-testid="sensor_mail_address">{{ displayedMailAddress }}</div>
            </template>
          </form-group>
          <form-group :required="state.editting">
            <template #label><span>{{ $mpt($t('field.name')) }}</span></template>
            <template #input>
              <text-field
                v-model="model.name"
                :edit="state.editting"
                :rules="rules.name"
                data-testid="sensor_name"
              />
            </template>
          </form-group>
          <form-group v-if="capabilities.hasPlace">
            <template #label><span>{{ placeLabel }}</span></template>
            <template #input>
              <text-field
                v-model="model.place"
                :edit="state.editting"
                data-testid="sensor_place"
              />
            </template>
          </form-group>
          <form-group :required="state.editting">
            <template #label><span>{{ $t('field.installation_date') }}</span></template>
            <template #input>
              <date-field
                v-model="model.installed_on"
                :edit="state.editting"
                :rules="rules.installed_on"
                data-testid="sensor_installed_on"
              />
            </template>
          </form-group>
          <form-group v-if="capabilities.threshold" :required="state.editting">
            <template #label><span>{{ $t('field.threshold') }}</span></template>
            <template #input>
              <text-field
                type="number"
                v-model="model.threshold"
                :edit="state.editting"
                :rules="rules.threshold"
              />
            </template>
          </form-group>
          <form-group v-if="capabilities.threshold" :required="state.editting">
            <template #label><span>{{ $t('field.increase_threshold') }}</span></template>
            <template #input>
              <text-field
                type="number"
                v-model="model.increase_threshold"
                :edit="state.editting"
                :rules="rules.increase_threshold"
              />
            </template>
          </form-group>
          <form-group v-if="capabilities.replacement">
            <template #label><span>{{ $t('field.trap_replaced_on') }}</span></template>
            <template #input>
              <date-field
                v-model="model.trap_replaced_on"
                clearable
                :edit="state.editting"
              />
            </template>
          </form-group>
          <form-group v-if="capabilities.replacement">
            <template #label><span>{{ $t('field.trap_effective_days') }}</span></template>
            <template #input>
              <text-field
                type="number"
                v-model="model.trap_effective_days"
                :edit="state.editting"
                :rules="rules.trap_effective_days"
              />
            </template>
          </form-group>
          <form-group v-if="capabilities.photoInterval">
            <template #label><span>{{ $t('field.photo_interval') }}</span></template>
            <template #input>
              <photo-interval-select-field
                v-model="model.photo_interval"
                :edit="state.editting"
              />
            </template>
          </form-group>
          <form-group>
            <template #label><span>{{ $t('field.memo') }}</span></template>
            <template #input>
              <text-field
                v-model="model.memo"
                :edit="state.editting"
                data-testid="sensor_memo"
              />
            </template>
          </form-group>
          <template v-if="!admin && capabilities.sendsNotifications">
            <form-group v-if="state.editting || shareable">
              <template #label><span>{{ $t('label.notifications') }}</span></template>
              <template #input>
                <checkbox
                  v-model="model.notice"
                  :label="$t('notification.by_email')"
                  :edit="state.editting"
                />
              </template>
            </form-group>
          </template>
          <form-group v-if="capabilities.health_check">
            <template #label><span>{{ $t('label.health_check') }}</span></template>
            <template #input>
              <div>
                <sensor-health-check-status
                  :status="sensor.health_check_status"
                  :canDownload="admin"
                  class="mt-1"
                />
                <v-row justify="start" class="mt-3" no-gutters>
                  <span class="mr-2">{{ $t('label.last_active') }}</span>
                  <span>{{ formatTimestamp(sensor.last_active) }}</span>
                </v-row>
              </div>
            </template>
          </form-group>
          <form-group
            data-testid="field_last-reception"
            v-if="!new_record"
          >
            <template #label><span>{{ $t('label.last_reception') }}</span></template>
            <template #input>
              <sensor-last-reception
                v-if="sensor.last_event_request_time"
                :sensor="sensor"
                :shows-signal-strength="showsSignalStrength"
              />
              <template v-else>{{ $t('timestamp.none') }}</template>
            </template>
          </form-group>
          <form-group v-if="!new_record" data-testid="field_created-by">
            <template #label><span>{{ $t('field.created_by') }}</span></template>
            <template #input>
              {{ sensor.created_by.name || '--' }}
            </template>
          </form-group>
        </v-form>
      </div>
      <div class="sensor-form__btn-group">
        <template v-if="state.editting">
          <round-button
            class="sensor-form__btn sensor-form__save my-3"
            icon="fa-check-circle"
            fullWidth
            :disabled="state.checkingId || state.saving"
            :loading="state.saving"
            @click="onSave"
            data-testid="save"
          >
            {{ $t('button.save') }}
          </round-button >
          <round-button
            class="sensor-form__btn my-3"
            icon="fa-arrow-circle-left"
            fullWidth
            @click="onCancel"
          >
            {{ $t('button.cancel') }}
          </round-button>
        </template>
        <template v-else>
          <div
            class="sensor-form__section sensor-form__section--video-display"
            data-testid="section-video-display"
            v-if="hasVideoDisplaySection"
          >
            <round-button
              class="sensor-form__btn my-3"
              icon="fa-chevron-circle-down"
              fullWidth
              :to="`/activities?sensorId=${sensor.id}`"
              v-if="capabilities.hasMedia"
              data-testid="show-all"
            >
              {{ $t('button.show_all', { dataType: getTerm(sensorDefinition.dataType, { plural: true }) }) }}
            </round-button>
            <upload-button
              fullWidth
              :acceptedFiletypes="acceptedFiletypes"
              :sensorId="sensor.id"
              v-if="uploadable"
              data-testid="upload-button"
            >
              {{ $t('button.upload', { dataType: getTerm(sensorDefinition.dataType) }) }}
            </upload-button>
          </div>
          <div
            class="sensor-form__section sensor-form__section--share"
            data-testid="section-share"
            v-if="editable && capabilities.shareable"
          >
            <sensor-user-list
              :sensorId="sensor.id"
              :shareable="!admin"
            />
          </div>
          <v-divider
            class="my-4"
            data-testid="divider-share-modify"
            v-if="editable && capabilities.shareable && hasAnyModifyAction"
          />
          <div
            class="sensor-form__section sensor-form__section--modify"
            data-testid="section-modify"
            v-if="hasAnyModifyAction"
          >
            <round-button
              class="sensor-form__btn sensor-form__edit my-3"
              icon="fa-pencil-square-o"
              fullWidth
              @click="onEdit"
              v-if="editable"
              data-testid="edit"
            >
              {{ $t('button.edit') }}
            </round-button>
            <round-button
              class="sensor-form__btn my-3"
              icon="fa-refresh"
              fullWidth
              @click.native.stop="confirmGenerateMail"
              v-if="capabilities.receives_mails && editable"
              :disabled="!mailRegeneratable"
              data-testid="generate-mail"
            >
              {{ $t('button.generate_mail') }}
            </round-button>
            <round-button
              v-if="canShowRelocateButton"
              class="sensor-form__btn sensor-form__device-relocation my-3"
              icon="fa-arrow-right-arrow-left"
              fullWidth
              @click="deviceRelocationDialog.visible = true"
              data-testid="device-relocation-button"
            >
              {{ $t('button.relocate_device') }}
            </round-button>
            <v-tooltip
              v-if="editable && featureIsActive('monitoring_point') && capabilities.canRelocate
                && sensor.status === 'inactive' && !state.editting"
              bottom
            >
              <template #activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs">
                  <round-button
                    class="sensor-form__btn sensor-form__device-relocation my-3"
                    icon="fa-arrow-right-arrow-left"
                    fullWidth
                    disabled
                    data-testid="device-relocation-button-disabled"
                  >
                    {{ $t('button.relocate_device') }}
                  </round-button>
                </div>
              </template>
              <span
                data-testid="device-relocation-hint"
                class="device-relocation-hint"
              >{{ $t('msg.no_device_hint') }}</span>
            </v-tooltip>
            <v-tooltip top :disabled="deletable" v-if="editable">
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="d-block"
                  v-bind="attrs"
                  v-on="on"
                >
                  <round-button
                    class="sensor-form__btn my-3"
                    icon="fa-trash-o"
                    fullWidth
                    :disabled="!deletable"
                    @click="confirmDelete"
                    data-testid="delete-button"
                  >
                    {{ $t('button.delete') }}
                  </round-button>
                </div>
              </template>
              <span style="font-size: 12px;">{{ $t(deleteDisabledSharedKey) }}</span>
            </v-tooltip>
            <round-button
              class="sensor-form__btn my-3"
              icon="fa-list"
              fullWidth
              :to="`/admin/activity_logs/Sensor-${sensor.id}`"
              v-if="showConnectionLogsButton"
              data-testid="show-connection-logs"
            >
              {{ $t('button.show_connection_logs') }}
            </round-button>
          </div>
        </template>
      </div>
    </div>
    <confirm-dialog
      v-model="confirmDialog.visible"
      :message="confirmDialog.message"
      @yes="confirmDialog.onConfirm"
    />
    <device-relocation-dialog
      v-if="featureIsActive('monitoring_point') && capabilities.canRelocate"
      v-model="deviceRelocationDialog.visible"
      :loading="relocatingDevice"
      :candidates-loading="loadingCandidates"
      :destination-candidates="transferDestinationCandidates"
      :source-name="sensor.name"
      :source-device-id="sensor.device_id"
      @relocated="onRelocated"
      @transferred="onTransferDevice"
      @inactive-mode-selected="onInactiveModeSelected"
    />
  </div>
</template>

<i18n lang="yaml">
ja:
  field:
    identifier: '識別子'
    name: 'センサー名'
    place: '設置場所'
    place_legacy: '設置場所（旧フィールド）'
    installation_date: '設置日'
    threshold: '累積アラート用しきい値'
    increase_threshold: '差分アラート用しきい値'
    memo: 'メモ'
    mail_address: 'メールアドレス'
    conditions: '状態'
    trap_replaced_on: 捕虫紙交換日
    trap_effective_days: 捕虫紙有効日数
    photo_interval: 撮影間隔
    created_by: 作成者

  label:
    new_sensor: 'センサー登録'
    notifications: '通知'
    health_check: 'ライフチェック'
    last_active: '最終接続日時'
    last_reception: '最終受信状況'

  term:
    images:
      singular: '画像'
      plural: '画像'
    videos:
      singular: '動画'
      plural: '動画'
    images_and_videos:
      singular: '画像/動画'
      plural: '画像/動画'
    none:
      singular: 'なし'
      plural: 'なし'

  notification:
    by_email: '通知をメールで送信'

  msg:
    delete_confirm: '<strong>⚠️このセンサーを削除します。<span style="color: red;">もとに戻すことができませんが、本当によろしいでしょうか？</span>⚠️</strong>'
    delete_confirm_mp: >-
      <strong>⚠️このモニタリングポイントを削除します。<span style="color: red;">もとに戻すことができませんが、本当によろしいでしょうか？</span>⚠️</strong>
    delete_disabled_shared: 'このセンサーは他のユーザーと共有されているため削除できません。削除するには、まず共有を解除してください。'
    delete_disabled_shared_mp: 'このモニタリングポイントは他のユーザーと共有されているため削除できません。削除するには、まず共有を解除してください。'
    generate_mail_confirm: 'このセンサーのメールアドレスを再生成します。よろしいですか？'
    generate_mail_confirm_mp: 'このモニタリングポイントのメールアドレスを再生成します。よろしいですか？'
    generate_mail_success: 'メールアドレスを再生成しました。'
    generate_mail_error: 'メールアドレスの再生成に失敗しました。'
    create_success: 'センサーを作成しました'
    create_error: 'センサーの作成中にエラーが発生しました'
    update_success: 'センサーを更新しました'
    update_error: 'センサーの更新中にエラーが発生しました'
    relocate_success: 'センサーを移設しました'
    relocate_error: 'センサーの移設に失敗しました'
    transfer_success: センサーを移設しました。移設先のモニタリングポイントの監視を再開しました。
    transfer_error: センサーの移設に失敗しました
    transfer_candidates_load_error: '移設先候補の取得に失敗しました。ネットワーク状況を確認してください。'
    no_device_hint: |-
      休止中のモニタリングポイントにはセンサーが配置されていません。
      監視を再開するには、監視中のモニタリングポイントから
      センサーを移設してください。
    id_unavailable: '入力された{label}は使えません。入力内容をご確認ください。'
    rat_sense_api_unavailable: 'RatSense の検証サービスが一時的に利用できません。'
    not_found: |
      指定されたセンサーが存在しません。
      識別子が間違っている、または共有が解除された可能性があります。

  button:
    cancel: 'キャンセル'
    delete: '削除'
    edit: '編集'
    ok: 'OK'
    upload: '{dataType}をアップロード'
    save: '保存'
    show_all: 'すべての{dataType}を表示'
    generate_mail: 'メールアドレスを再生成'
    show_connection_logs: 通信記録を表示
    relocate_device: 'センサーの移設'

  hint:
    identifier: '半角英数字と_(アンダーバー)で入力してください'
    rat_sense_mp_id: '半角数字のみで入力してください'
  timestamp:
    none: なし

en:
  field:
    identifier: 'Identifier'
    name: 'Sensor name'
    place: 'Installation site'
    place_legacy: 'Installation site (legacy field)'
    installation_date: 'Installation date'
    threshold: 'Cumulative Alert Threshold'
    increase_threshold: 'Differential Alert Threshold'
    memo: 'Notes'
    mail_address: 'Mail address'
    conditions: 'Condition'
    trap_replaced_on: Trap replaced on
    trap_effective_days: Trap effective days
    photo_interval: Photo Interval
    created_by: Created by

  label:
    new_sensor: 'New sensor'
    notifications: 'Notifications'
    health_check: 'Health check'
    last_active: 'Last active'
    last_reception: 'Last received'

  term:
    images:
      singular: 'Image'
      plural: 'Images'
    videos:
      singular: 'Video'
      plural: 'Videos'
    images_and_videos:
      singular: 'Image/Video'
      plural: 'Images/Videos'
    none:
      singular: 'None'
      plural: 'None'

  notification:
    by_email: 'E-mail notification'

  msg:
    delete_confirm: >-
      <strong>⚠️ This sensor will be deleted
      <span style="color: red;">and cannot be restored. Are you sure you want to proceed?</span>
      ⚠️</strong>
    delete_confirm_mp: >-
      <strong>⚠️ This Monitoring Point will be deleted
      <span style="color: red;">and cannot be restored. Are you sure you want to proceed?</span>
      ⚠️</strong>
    delete_disabled_shared: >
      This sensor cannot be deleted because it is shared with other users.
      Please unshare it first before deleting.
    delete_disabled_shared_mp: >
      This Monitoring Point cannot be deleted because it is shared with other users.
      Please unshare it first before deleting.
    generate_mail_confirm: 'The mail address of this sensor will be newly generated. Are you sure?'
    generate_mail_confirm_mp: 'The mail address of this Monitoring Point will be newly generated. Are you sure?'
    generate_mail_success: 'A new mail address was successfully generated.'
    generate_mail_error: 'Failed to generate a new mail address.'
    create_success: 'Sensor was successfully created'
    create_error: 'An error occurred while creating the sensor'
    update_success: 'Sensor was successfully updated'
    update_error: 'An error occurred while updating the sensor'
    relocate_success: 'Sensor relocated'
    relocate_error: 'Failed to relocate sensor'
    transfer_success: 'Sensor relocated. Monitoring resumed on the destination Monitoring Point.'
    transfer_error: 'Failed to relocate sensor'
    transfer_candidates_load_error: 'Failed to load relocation destination candidates. Check your network.'
    no_device_hint: |-
      This inactive Monitoring Point has no sensor.
      To resume monitoring, relocate a sensor
      from an active Monitoring Point.
    id_unavailable: 'This {label} cannot be used. Please check your input.'
    rat_sense_api_unavailable: 'The RatSense verification service is temporarily unavailable.'
    not_found: |
      The specified sensor does not exist.
      Either the wrong id was specified or your permissions on this sensor have been revoked.

  button:
    cancel: 'Cancel'
    delete: 'Delete'
    edit: 'Edit'
    ok: 'OK'
    upload: 'Upload {dataType}'
    save: 'Save'
    show_all: 'Show {dataType}'
    generate_mail: 'Generate mail address'
    show_connection_logs: Show connection logs
    relocate_device: 'Relocate Sensor'

  hint:
    identifier: 'The identifier can contain alphanumeric characters or underscore'
    rat_sense_mp_id: 'The identifier must contain only digits'
  timestamp:
    none: Never
</i18n>

<script>
import DateTimeFormatting from '@/mixins/dateTimeFormatting';
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';
import SensorTypes from '@/mixins/sensorTypes';

import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import EntityHeader from '@/components/atoms/EntityHeader';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import RoundButton from '@/components/atoms/RoundButton';
import StatusChip from '@/components/atoms/StatusChip';
import PhotoIntervalSelectField from '@/components/molecules/PhotoIntervalSelectField';
import SensorLastReception from '@/components/molecules/SensorLastReception';
import Counter from '@/components/ContentHeader/Counter';
import SensorIcon from '@/components/SensorIcon';
import FormGroup from '@/components/Form/FormGroup';
import TextField from '@/components/Form/TextField';
import DateField from '@/components/Form/DateField';
import Checkbox from '@/components/Form/Checkbox';
import Validations from '@/components/Form/mixins/form-validations';
import SensorHealthCheckStatus from '@/components/organisms/SensorHealthCheckStatus';

import UploadButton from './UploadButton';
import SensorUserList from './SensorUserList';
import DeviceRelocationDialog from './DeviceRelocationDialog';

// 識別子入力の書式（capabilities.identifierFormat）ごとに、ヒント文言と
// 入力チェックの正規表現を 1 か所から解決するためのマッピング。
// 追加時は sensorTypes.js の identifierFormat 値と、`hint` 側の i18n キーを合わせる。
const IDENTIFIER_FORMATS = {
  alphanumeric_underscore: {
    hintKey: 'hint.identifier',
    pattern: /^[a-zA-Z0-9_]+$/,
  },
  digits_only: {
    hintKey: 'hint.rat_sense_mp_id',
    pattern: /^[0-9]+$/,
  },
};

export default {
  name: 'sensor-form',
  mixins: [
    DateTimeFormatting,
    Features,
    SensorTypes,
    Validations,
    Notifications,
  ],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    new_record: {
      type: Boolean,
      default: false,
    },
  },
  beforeMount: async function () {
    if (this.$route.params.id) {
      try {
        await this.$store.dispatch('requestSensor', this.$route.params.id);
      } catch (error) {
        this.notifyError('msg.not_found');
      }
    }
  },
  components: {
    Checkbox,
    ConfirmDialog,
    Counter,
    DateField,
    DeviceRelocationDialog,
    EntityHeader,
    FormGroup,
    PageTitleArea,
    PhotoIntervalSelectField,
    RoundButton,
    SensorIcon,
    SensorHealthCheckStatus,
    SensorLastReception,
    SensorUserList,
    StatusChip,
    TextField,
    UploadButton,
  },
  computed: {
    acceptedFiletypes() {
      return (this.capabilities.upload || { accept: [] }).accept;
    },
    capabilities() {
      return this.sensorDefinition.capabilities;
    },
    count() {
      return this.sensor.type === this.SENSOR_TYPES.RAT
        ? `${this.sensor.undetected_event_count} / ${this.sensor.total_event_count}`
        : this.sensor.total_event_count;
    },
    domain() {
      return this.appConfig.DOMAIN;
    },
    editable() {
      return this.admin || this.sensor.permissions.includes('full');
    },
    hasAnyModifyAction() {
      return this.editable
        || this.canShowRelocateButton
        || this.showConnectionLogsButton;
    },
    hasVideoDisplaySection() {
      return this.capabilities.hasMedia || this.uploadable;
    },
    isProduction() {
      return this.appConfig.DOMAIN === 'pest-vision.com';
    },
    sensor() {
      if (this.new_record) {
        const { type } = this.$route.query;
        return { type };
      }
      return this.$store.getters.getSensor(this.$route.params.id);
    },
    sensorDefinition() {
      return this.SENSOR_DEFINITIONS[this.sensor.type];
    },
    sensorId() {
      // 新規作成中は入力中の識別子、既存は機器の識別子。内部 ID は表示しない
      if (this.new_record) return this.model.id;
      return this.sensor.device_id;
    },
    isInactiveMonitoringPoint() {
      return this.featureIsActive('monitoring_point') && this.sensor.status === 'inactive';
    },
    identifierVisible() {
      if (this.new_record) return true;
      if (this.admin) {
        return this.sensor.status !== 'inactive';
      }
      return !this.isInactiveMonitoringPoint;
    },
    displayedSensorId() {
      return this.identifierVisible ? this.sensorId : '';
    },
    mailAddressVisible() {
      if (!this.sensor.device_id) return false;
      if (this.admin) {
        return this.sensor.status !== 'inactive';
      }
      return !this.isInactiveMonitoringPoint;
    },
    // 休止中のモニタリングポイントは受信が止まっているのが正常で、最後に受信したときの
    // 通信強度を出すと今の状態のように読めるため出さない。一覧のツールチップと同じ条件で判定する。
    showsSignalStrength() {
      return !this.isInactiveMonitoringPoint;
    },
    mailAddress() {
      return `${this.sensor.device_id}-${this.sensor.mail_hash}@sensor.${this.domain}`;
    },
    displayedMailAddress() {
      return this.mailAddressVisible ? this.mailAddress : '';
    },
    mailRegeneratable() {
      return this.sensor.status !== 'inactive' && !this.pendingGenerateMail;
    },
    shareable() {
      return this.editable;
    },
    showConnectionLogsButton() {
      // Only show on production because only production logs are collected.
      // On other environments the sensor id will probably not exist.
      // Connection logs are restricted to administrators, so distributors cannot open them.
      return this.admin && this.$store.getters.getCurrentUser.admin && this.isProduction;
    },
    uploadable() {
      return this.capabilities.upload && this.editable;
    },
    rules() {
      return {
        id: [
          this.required(this.$mpt(this.identifierLabel)),
          this.format(this.$mpt(this.identifierLabel), this.identifierPattern),
        ],
        name: [this.required(this.$mpt(this.$t('field.name')))],
        installed_on: [this.required(this.$t('field.installation_date'))],
        threshold: [
          this.required(this.$t('field.threshold')),
          this.positive(this.$t('field.threshold')),
        ],
        increase_threshold: [
          this.required(this.$t('field.increase_threshold')),
          this.positive(this.$t('field.increase_threshold')),
        ],
        trap_effective_days: [
          this.within_range(this.$t('field.trap_effective_days'), 0, 60),
        ],
      };
    },
    isSensorShared() {
      if (this.new_record) {
        return false;
      }
      const sharedUsers = this.$store.getters.getSensorUsers(this.sensor.id);
      return sharedUsers && sharedUsers.length > 0;
    },
    deletable() {
      return this.editable && !this.isSensorShared;
    },
    placeLabel() {
      const key = this.featureIsActive('monitoring_point')
        ? 'field.place_legacy'
        : 'field.place';
      return this.$t(key);
    },
    identifierLabel() {
      const key = this.capabilities.identifierLabelKey || 'field.identifier';
      return this.$t(key);
    },
    idUnavailableMessage() {
      return this.$t('msg.id_unavailable', { label: this.$mpt(this.identifierLabel) });
    },
    identifierHint() {
      const spec = IDENTIFIER_FORMATS[this.capabilities.identifierFormat]
        || IDENTIFIER_FORMATS.alphanumeric_underscore;
      return this.$t(spec.hintKey);
    },
    identifierPattern() {
      const spec = IDENTIFIER_FORMATS[this.capabilities.identifierFormat]
        || IDENTIFIER_FORMATS.alphanumeric_underscore;
      return spec.pattern;
    },
    canShowRelocateButton() {
      return this.editable
        && this.featureIsActive('monitoring_point')
        && this.capabilities.canRelocate
        && this.sensor.status === 'active'
        && !this.state.editting;
    },
    deleteConfirmKey() {
      return this.featureIsActive('monitoring_point')
        ? 'msg.delete_confirm_mp'
        : 'msg.delete_confirm';
    },
    deleteDisabledSharedKey() {
      return this.featureIsActive('monitoring_point')
        ? 'msg.delete_disabled_shared_mp'
        : 'msg.delete_disabled_shared';
    },
    generateMailConfirmKey() {
      return this.featureIsActive('monitoring_point')
        ? 'msg.generate_mail_confirm_mp'
        : 'msg.generate_mail_confirm';
    },
    transferDestinationCandidates() {
      return this.$store.getters.getSensors
        .filter(s => s.status === 'inactive' && s.type === this.sensor.type);
    },
  },
  data() {
    return {
      show: false,
      valid: false,
      model: {},
      pendingGenerateMail: false,
      confirmDialog: {
        visible: false,
        message: '',
        onConfirm: () => {},
      },
      deviceRelocationDialog: {
        visible: false,
      },
      relocatingDevice: false,
      loadingCandidates: false,
      state: {
        editting: this.new_record,
        checkingId: false,
        saving: false,
        idErrors: [],
      },
      checkAvailabilityWhenFinishedTyping: this._.debounce(this.checkAvailability, 500),
      conditions: {},
    };
  },
  inject: ['appConfig'],
  methods: {
    checkAvailability: async function () {
      if (!this.model.id) {
        return;
      }
      const errors = [];
      // 利用できない理由 (登録済み / RatSense に実在しない) はサーバー側で
      // 単一の可否に畳まれる。区別できる状態で受け取ると、その組み合わせから
      // 「実在するが使えない = 他組織が使用中」を判別できてしまうため。
      try {
        const available = await this.$store.dispatch('checkAvailability', {
          id: this.model.id,
          type: this.sensor.type,
        });
        if (!available) {
          errors.push(this.idUnavailableMessage);
        }
      } catch (error) {
        // RatSense は実在確認のため外部サービスに問い合わせるため、
        // その不通時のみ復旧待ちと判断できる文言を出す。
        if (this.sensor.type === this.SENSOR_TYPES.RAT_SENSE) {
          errors.push(this.$t('msg.rat_sense_api_unavailable'));
        }
      }

      this.state.idErrors = errors;
      this.state.checkingId = false;
    },
    checkNewSensorType(type) {
      if (!this.$store.getters.hasPermission(type)) {
        this.redirectToSensorList();
      }
    },
    confirmDelete() {
      this.confirmDialog.message = this.$t(this.deleteConfirmKey);
      this.confirmDialog.onConfirm = this.onDelete;
      this.confirmDialog.visible = true;
    },
    confirmGenerateMail() {
      this.confirmDialog.message = this.$t(this.generateMailConfirmKey);
      this.confirmDialog.onConfirm = this.onGenerateMail;
      this.confirmDialog.visible = true;
    },
    getTerm(term, options = { plural: false }) {
      const termType = options.plural ? 'plural' : 'singular';
      return this.$t(`term.${term}.${termType}`);
    },
    initializeModel(type) {
      const result = {
        type,
        id: '',
        name: '',
        place: '',
        installed_on: null,
        memo: '',
        notice: false,
      };
      const { capabilities } = this.SENSOR_DEFINITIONS[type];
      if (capabilities.threshold) {
        result.threshold = 0;
        result.increase_threshold = 0;
      }
      if (capabilities.replacement) {
        result.trap_effective_days = 30; // 30 days
      }
      if (capabilities.photoInterval) {
        result.photo_interval = 3600; // 1 hour
      }
      this.model = result;
    },
    isIdUnavailableError(error) {
      return Boolean(error.response
        && error.response.data
        && error.response.data.type === 'sensor_id_unavailable');
    },
    onCancel() {
      if (this.new_record) {
        this.redirectToSensorList();
        return;
      }
      this.setModel();
      this.state.editting = false;
    },
    onDelete: async function () {
      try {
        await this.$store.dispatch('requestDestroySensor', this.sensor.id);
        this.redirectToSensorList();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error === 'shared_sensor_cannot_be_deleted') {
          this.notifyError(this.deleteDisabledSharedKey);
        } else {
          this.notifyError('msg.update_error');
        }
      }
    },
    onGenerateMail: async function () {
      this.pendingGenerateMail = true;
      try {
        await this.$store.dispatch('requestUpdateSensor', { id: this.sensor.id, data: { generate_hash: true } });
        this.notifySuccess('msg.generate_mail_success');
      } catch (error) {
        this.notifyError('msg.generate_mail_error');
      } finally {
        this.pendingGenerateMail = false;
      }
    },
    onEdit() {
      this.state.editting = true;
    },
    onSave: async function () {
      // 応答を待つ間に続けて押すと、同じ識別子のモニタリングポイントが重複して登録される
      if (this.state.saving || !this.$refs.form.validate()) {
        return;
      }
      this.state.saving = true;
      try {
        const data = this.parseParams(this.model);
        if (this.new_record) {
          try {
            const created = await this.$store.dispatch('requestCreateSensor', { ...data, type: this.sensor.type });
            this.notifySuccess('msg.create_success');
            this.redirectToSensor(created.id);
            this.state.editting = false;
          } catch (error) {
            if (this.isIdUnavailableError(error)) {
              this.notifyError('msg.id_unavailable', { label: this.$mpt(this.identifierLabel) });
            } else {
              this.notifyError('msg.create_error');
            }
          }
        } else {
          try {
            await this.$store.dispatch('requestUpdateSensor', { id: this.sensor.id, data });
            this.notifySuccess('msg.update_success');
            this.redirectToSensor(this.sensor.id);
            this.state.editting = false;
          } catch (error) {
            this.notifyError('msg.update_error');
          }
        }
      } finally {
        this.state.saving = false;
      }
    },
    parseParams(params) {
      const result = {
        ...params,
      };
      Object.keys(result).forEach((attribute) => {
        if (result[attribute] === this.sensor[attribute]) {
          delete result[attribute];
        }
      });
      return result;
    },
    redirectToSensorList() {
      if (this.admin) {
        this.$router.push('/admin/sensors');
      } else {
        this.$router.push('/sensors');
      }
    },
    redirectToSensor(id) {
      if (this.admin) {
        this.$router.push(`/admin/sensors/${id}`);
      } else {
        this.$router.push(`/sensors/${id}`);
      }
    },
    setModel() {
      if (this.sensor) {
        this.initializeModel(this.sensor.type);
        this.model = { ...this.model, ...this.sensor };
        if (this._.isNil(this.sensor.memo)) {
          this.model.memo = '';
        }
      }
    },
    updateId(id) {
      this.model.id = id;
      this.state.checkingId = true;
      this.checkAvailabilityWhenFinishedTyping();
    },
    async onInactiveModeSelected() {
      this.loadingCandidates = true;
      try {
        await this.$store.dispatch('requestSensors');
      } catch (error) {
        this.notifyError('msg.transfer_candidates_load_error');
      } finally {
        this.loadingCandidates = false;
      }
    },
    async onTransferDevice({ destination_id: destinationId }) {
      this.relocatingDevice = true;
      try {
        const { destination } = await this.$store.dispatch('requestTransferDevice', {
          id: this.sensor.id,
          data: { destination_id: destinationId },
        });
        this.notifySuccess('msg.transfer_success');
        // 実行後にダイアログを閉じた場合、移設自体は成立しているので結果は通知するが、
        // 画面から離れる意思表示とみなして移設先への遷移は行わない
        if (!this.deviceRelocationDialog.visible) {
          return;
        }
        this.deviceRelocationDialog.visible = false;
        this.$router.push({ name: 'sensor', params: { id: destination.id } });
      } catch (error) {
        this.notifyError('msg.transfer_error');
      } finally {
        this.relocatingDevice = false;
      }
    },
    async onRelocated({ name, copy_share: copyShare }) {
      this.relocatingDevice = true;
      try {
        const newSensor = await this.$store.dispatch('requestRelocateSensor', {
          id: this.sensor.id,
          data: { name, copy_share: copyShare },
        });
        this.notifySuccess('msg.relocate_success');
        // 実行後にダイアログを閉じた場合、移設自体は成立しているので結果は通知するが、
        // 画面から離れる意思表示とみなして移設先への遷移は行わない
        if (!this.deviceRelocationDialog.visible) {
          return;
        }
        this.deviceRelocationDialog.visible = false;
        this.$router.push({ name: 'sensor', params: { id: newSensor.id } });
      } catch (error) {
        this.notifyError('msg.relocate_error');
      } finally {
        this.relocatingDevice = false;
      }
    },
  },
  async created() {
    // Initialize for new sensor creation
    if (this.new_record) {
      const { type } = this.$route.query;
      this.checkNewSensorType(type);
      this.initializeModel(type);
      this.show = true;
    }

    // Load shared users for existing sensors to enable delete button control
    if (!this.new_record && this.sensor && this.sensor.id) {
      await this.$store.dispatch('requestSensorUsers', this.sensor.id);
    }
  },
  watch: {
    sensor(val) {
      this.setModel();
      this.show = !!val;
      // Load shared users when sensor data is loaded
      if (val && val.id && !this.new_record) {
        this.$store.dispatch('requestSensorUsers', val.id);
      }
    },
    '$route.params.id'(newId) {
      if (this.sensor && this.sensor.id === newId) return;
      this.$store.dispatch('requestSensor', newId);
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'

.sensor-form
  &__btn-group
    margin: 24px 0

  &__section
    margin-bottom: 16px

    &--share
      background-color: #e0e0e0
      padding: 12px

  ::v-deep #image-upload
    display: none

  ::v-deep .v-select,
  ::v-deep .v-text-field
    .v-input__control .v-input__slot
      background-color: transparent

  ::v-deep .page-title-area__title
    min-width: 0

@media #{map-get($display-breakpoints, 'md-and-up')}
  .sensor-form
    &__content
      display: flex

    &__form
      width: 50%

    &__btn-group
      margin: 0 40px
      width: 320px

    &__btn ::v-deep .v-btn__content
      padding: 0 32px

.device-relocation-hint
  white-space: pre-line
</style>
